import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Game2048.css";
import { toast } from "react-toastify";
import animationData from "./animation.json";
import Lottie from "react-lottie";
import Popup from "../Popup2";
import { updateDocumentInSubcollection } from "../../firestore_sub"; // Import the Firestore function
import { auth } from "../../firebase"; // Import auth for uid access
import { updateDocument, getDocument } from "../../firestore";
import styles from "../games.module.css";
import { MdOutlineRestartAlt } from "react-icons/md";
import { FaPlay, FaPause } from "react-icons/fa";
import GameOverScreen from "../GameOverScreen";

export default function Game2048() {
  const [lives, setLives] = useState(3); // Start with 3 lives
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Pause state for game when popup is active
  const [showPopup, setShowPopup] = useState(false); // Control popup visibility
  const uid = auth.currentUser ? auth.currentUser.uid : null;
  const [score, setScore] = useState(0);
  const gameOverRef = useRef(false);

  useEffect(() => {
    checkGameStarted();
  }, []);

  // Timer to show popup at regular intervals
  useEffect(() => {
    if (!gameOverRef.current && lives > 0 && !showPopup) {
      const popupTimer = setTimeout(() => {
        setShowPopup(true); // Show popup immediately
        setIsPaused(true); // Pause game immediately when popup appears
      }, 20000); // 20 seconds between popups

      return () => clearTimeout(popupTimer); // Cleanup timer on unmount
    }
  }, [gameOverRef.current, lives, showPopup]);

  // Close popup and resume game
  const handleClosePopup = () => {
    setShowPopup(false); // Close popup
    setIsPaused(false); // Resume game after popup is closed
  };

  const handlePopupIncorrect = () => {
    setLives((prevLives) => {
      const updatedLives = prevLives;
      if (updatedLives <= 0) {
        setGameOver(true);
        return 0;
      }
      return updatedLives;
    });
  };

  useEffect(() => {
    if (lives <= 0) {
      setGameOver(true);
      setIsPaused(true);
    }
  }, [lives]);

  async function checkGameStarted() {
    const queryParams = new URLSearchParams(window.location.search);
    const gameId = queryParams.get("id");
    if (!gameId) return;

    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;

    try {
      const gameDoc = await getDocument(subCollectionPath, gameId);
      console.log("Game Doc:", gameDoc);
      if (gameDoc?.gameStarted) {
        const redirectUrl = `${window.location.origin}/instructions/2048`;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error checking game status:", error);
    }
  }

  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = score;
    const gameplayData = {
      lives: lives,
      score: currentScore,
      // total_questions : total_questions
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
      // total_questions : total_questions
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };
  const [board, setBoard] = useState(
    Array.from({ length: 4 }, () => Array.from({ length: 4 }, () => 0))
  );

  const checkGameOver = useCallback(() => {
    if (lives <= 0) return true;
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[i].length; j++) {
        if (board[i][j] === 0) {
          return false;
        }
      }
    }
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[i].length; j++) {
        if (
          (i < board.length - 1 && board[i][j] === board[i + 1][j]) ||
          (j < board[i].length - 1 && board[i][j] === board[i][j + 1])
        ) {
          return false;
        }
      }
    }
    return true;
  }, [board]);

  useEffect(() => {
    if (checkGameOver()) {
      gameOverRef.current = true;
    }
  }, [board]);

  const restartGame = () => {
    setBoard(
      Array.from({ length: 4 }, () => Array.from({ length: 4 }, () => null))
    );
    setScore(0);
    setLives(3); // Reset lives
    generateRandomTile();
    generateRandomTile();
    gameOverRef.current = false;
  };

  const generateRandomTile = useCallback(() => {
    let emptyTiles = [];
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[i].length; j++) {
        if (board[i][j] === 0) {
          emptyTiles.push({ x: i, y: j });
        }
      }
    }
    if (emptyTiles.length === 0) {
      gameOverRef.current = true; // Update gameOver using useRef
      return;
    }
    const randomIndex = Math.floor(Math.random() * emptyTiles.length);
    const newValue = Math.random() < 0.9 ? 2 : 4;
    const { x, y } = emptyTiles[randomIndex];
    setBoard((prevBoard) => {
      const newBoard = [...prevBoard];
      newBoard[x][y] = newValue;
      return newBoard;
    });
    // setGameOver(checkGameOver());
    gameOverRef.current = checkGameOver(); // Update gameOver using useRef
  }, [board, checkGameOver]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!gameOverRef.current) {
        e.preventDefault();
        let moved = false;
        const newBoard = [...board];

        switch (e.key) {
          case "ArrowUp":
            for (let j = 0; j < newBoard.length; j++) {
              for (let i = 1; i < newBoard.length; i++) {
                if (newBoard[i][j] !== 0) {
                  let k = i - 1;
                  while (k >= 0 && newBoard[k][j] === 0) {
                    k--;
                  }
                  if (k >= 0 && newBoard[k][j] === newBoard[i][j]) {
                    newBoard[k][j] *= 2;
                    setScore((prevScore) => prevScore + newBoard[k][j]);
                    newBoard[i][j] = 0;
                    moved = true;
                  } else if (k + 1 !== i) {
                    newBoard[k + 1][j] = newBoard[i][j];
                    newBoard[i][j] = 0;
                    moved = true;
                  }
                }
              }
            }
            break;
          case "ArrowDown":
            for (let j = 0; j < newBoard.length; j++) {
              for (let i = newBoard.length - 2; i >= 0; i--) {
                if (newBoard[i][j] !== 0) {
                  let k = i + 1;
                  while (k < newBoard.length && newBoard[k][j] === 0) {
                    k++;
                  }
                  if (
                    k < newBoard.length &&
                    newBoard[k][j] === newBoard[i][j]
                  ) {
                    newBoard[k][j] *= 2;
                    setScore((prevScore) => prevScore + newBoard[k][j]);
                    newBoard[i][j] = 0;
                    moved = true;
                  } else if (k - 1 !== i) {
                    newBoard[k - 1][j] = newBoard[i][j];
                    newBoard[i][j] = 0;
                    moved = true;
                  }
                }
              }
            }
            break;
          case "ArrowLeft":
            for (let i = 0; i < newBoard.length; i++) {
              for (let j = 1; j < newBoard[i].length; j++) {
                if (newBoard[i][j] !== 0) {
                  let k = j - 1;
                  while (k >= 0 && newBoard[i][k] === 0) {
                    k--;
                  }
                  if (k >= 0 && newBoard[i][k] === newBoard[i][j]) {
                    newBoard[i][k] *= 2;
                    setScore((prevScore) => prevScore + newBoard[i][k]);
                    newBoard[i][j] = 0;
                    moved = true;
                  } else if (k + 1 !== j) {
                    newBoard[i][k + 1] = newBoard[i][j];
                    newBoard[i][j] = 0;
                    moved = true;
                  }
                }
              }
            }
            break;
          case "ArrowRight":
            for (let i = 0; i < newBoard.length; i++) {
              for (let j = newBoard[i].length - 2; j >= 0; j--) {
                if (newBoard[i][j] !== 0) {
                  let k = j + 1;
                  while (k < newBoard[i].length && newBoard[i][k] === 0) {
                    k++;
                  }
                  if (
                    k < newBoard[i].length &&
                    newBoard[i][k] === newBoard[i][j]
                  ) {
                    newBoard[i][k] *= 2;
                    setScore((prevScore) => prevScore + newBoard[i][k]);
                    newBoard[i][j] = 0;
                    moved = true;
                  } else if (k - 1 !== j) {
                    newBoard[i][k - 1] = newBoard[i][j];
                    newBoard[i][j] = 0;
                    moved = true;
                  }
                }
              }
            }
            break;
          default:
            return;
        }

        if (moved) {
          setBoard(newBoard);
          generateRandomTile();
          // setGameOver(checkGameOver());
          gameOverRef.current = checkGameOver(); // Update gameOver using useRef
          if (gameOverRef.current || gameOver) {
            toast.error(`Game Over! Final Score: ${score}`, {
              position: "top-center",
            });
          }
        }
      }
    },
    [board, checkGameOver, generateRandomTile, score]
  );

  useEffect(() => {
    generateRandomTile();
    generateRandomTile();
  }, []);

  const redirectMain = () => {
    window.location.href = "/games";
  };

  useEffect(() => {
    if (!isPaused) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, isPaused]);

  const exitGame = () => {
    window.location.href = "/games";
  };

  const renderHearts = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${
          index < lives ? "opacity-100" : "opacity-30"
        }`}
      >
        ❤️
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Game Title with Gradient */}
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          2048
        </h1>

        {/* Game Stats and Controls */}
        <div className="text-white text-3xl w-full flex justify-between items-center p-4">
          <div className="flex items-center gap-6">
            <h1 className="text-2xl font-bold text-[var(--text-color)]">
              Score: {score}
            </h1>
            <h1 className="text-2xl font-bold text-[var(--text-color)]">
              Lives: {renderHearts()}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <button
              className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
              onClick={restartGame}
            >
              <MdOutlineRestartAlt className="mr-2 text-xl" />
              Restart
            </button>

            <button
              className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
              onClick={exitGame}
            >
              Exit
            </button>
          </div>
        </div>

        {/* Game Board */}
        <div className="bg-[var(--surface-color)] rounded-2xl shadow-lg overflow-hidden border border-[var(--border-color)] p-6">
          {!checkGameOver() ? (
            <div className="game-board">
              {board.map((row, rowIndex) => (
                <div key={rowIndex} className="board-row">
                  {row.map((cell, colIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`board-cell value-${
                        cell !== 0 ? cell : "empty"
                      }`}
                    >
                      {cell !== 0 ? cell : ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <h1 className="text-4xl font-bold text-[var(--error-color)] mb-4">
                Game Over!
              </h1>
              <Lottie options={defaultOptions} height={400} width={400} />
            </div>
          )}
        </div>

        {/* Popup */}
        {showPopup && (
          <Popup
            onClose={handleClosePopup}
            onIncorrectAnswer={handlePopupIncorrect}
            score={score}
            setScore={setScore}
            lives={lives}
            setLives={setLives}
            useLives={true}
            gameOver={gameOver || gameOverRef.current}
            setIsPause={setIsPaused}
          />
        )}

        {gameOver && (
          <GameOverScreen
            score={score}
            onRestart={restartGame}
            onExit={exitGame}
          />
        )}
      </div>
    </div>
  );
}
